import {
	type MaybeRefOrGetter,
	nextTick,
	onBeforeUnmount,
	type Ref,
	ref,
	toValue,
	watch,
} from 'vue';

const DEFAULT_PAGE_LIMIT = 25;
const DEFAULT_ROOT_MARGIN = '120px';

export interface InfiniteScrollPage<T> {
	items: T[];
	total: number;
}

export interface InfiniteScrollSnapshot<T> {
	items: T[];
	total: number;
	nextOffset: number;
	hasNextPage: boolean;
}

export interface UseInfiniteScrollOptions<T> {
	pageLimit?: number;
	rootMargin?: string;
	initialLoading?: boolean;
	getItemId: (item: T) => string;
	fetchPage: (params: {
		offset: number;
		limit: number;
	}) => Promise<InfiniteScrollPage<T>>;
	filterItem?: (item: T) => boolean;
	onError?: (error: unknown, context: 'initial' | 'more') => void;
	/** Extra condition before attaching the observer (e.g. list is visible). */
	canObserve?: MaybeRefOrGetter<boolean>;
}

export const useInfiniteScroll = <T>(options: UseInfiniteScrollOptions<T>) => {
	const pageLimit = options.pageLimit ?? DEFAULT_PAGE_LIMIT;
	const rootMargin = options.rootMargin ?? DEFAULT_ROOT_MARGIN;

	const items: Ref<T[]> = ref([]);
	const total = ref(0);
	const nextOffset = ref(0);
	const hasNextPage = ref(false);
	const isLoading = ref(options.initialLoading ?? false);
	const isLoadingMore = ref(false);
	const loadError = ref(false);
	const scrollRootEl = ref<HTMLElement | null>(null);
	const loadMoreTriggerEl = ref<HTMLElement | null>(null);

	let fetchGeneration = 0;
	let intersectionObserver: IntersectionObserver | null = null;

	const disconnectLoadMoreObserver = () => {
		intersectionObserver?.disconnect();
		intersectionObserver = null;
	};

	const hasMorePages = (
		offset: number,
		fetchedCount: number,
		pageTotal: number
	): boolean => offset + fetchedCount < pageTotal;

	const filterFetchedItems = (fetchedItems: T[]): T[] => {
		if (!options.filterItem) return fetchedItems;
		return fetchedItems.filter((item) => options.filterItem?.(item) ?? true);
	};

	const replaceItems = (fetchedItems: T[]) => {
		items.value = filterFetchedItems(fetchedItems);
	};

	const appendItems = (fetchedItems: T[]) => {
		const nextItems = filterFetchedItems(fetchedItems);
		const knownIds = new Set(
			items.value.map((item) => options.getItemId(item))
		);
		const uniqueItems = nextItems.filter(
			(item) => !knownIds.has(options.getItemId(item))
		);
		if (uniqueItems.length === 0) return;
		items.value = [...items.value, ...uniqueItems];
	};

	const resetListState = () => {
		items.value = [];
		total.value = 0;
		nextOffset.value = 0;
		hasNextPage.value = false;
		loadError.value = false;
	};

	const reload = async (): Promise<void> => {
		const generation = ++fetchGeneration;
		disconnectLoadMoreObserver();
		isLoading.value = true;
		isLoadingMore.value = false;
		resetListState();

		try {
			const response = await options.fetchPage({
				offset: 0,
				limit: pageLimit,
			});
			if (generation !== fetchGeneration) return;

			replaceItems(response.items);
			total.value = response.total;
			nextOffset.value = pageLimit;
			hasNextPage.value = hasMorePages(
				0,
				response.items.length,
				response.total
			);
		} catch (error: unknown) {
			if (generation !== fetchGeneration) return;
			loadError.value = true;
			options.onError?.(error, 'initial');
		} finally {
			if (generation === fetchGeneration) {
				isLoading.value = false;
			}
		}
	};

	const loadMore = async (): Promise<void> => {
		if (!hasNextPage.value || isLoading.value || isLoadingMore.value) return;

		const generation = fetchGeneration;
		const requestOffset = nextOffset.value;
		isLoadingMore.value = true;

		try {
			const response = await options.fetchPage({
				offset: requestOffset,
				limit: pageLimit,
			});
			if (generation !== fetchGeneration) return;

			appendItems(response.items);
			nextOffset.value = requestOffset + pageLimit;
			hasNextPage.value = hasMorePages(
				requestOffset,
				response.items.length,
				response.total
			);
		} catch (error: unknown) {
			if (generation !== fetchGeneration) return;
			options.onError?.(error, 'more');
		} finally {
			if (generation === fetchGeneration) {
				isLoadingMore.value = false;
			}
		}
	};

	const setupLoadMoreObserver = async () => {
		disconnectLoadMoreObserver();

		const canObserve = options.canObserve ? toValue(options.canObserve) : true;
		if (isLoading.value || !hasNextPage.value || !canObserve) return;

		await nextTick();
		const loadMoreTrigger = loadMoreTriggerEl.value;
		const root = scrollRootEl.value;
		if (!loadMoreTrigger || !root) return;

		intersectionObserver = new IntersectionObserver(
			(entries) => {
				if (!entries.some((entry) => entry.isIntersecting)) return;
				void loadMore();
			},
			{ root, rootMargin, threshold: 0 }
		);
		intersectionObserver.observe(loadMoreTrigger);
	};

	watch(
		[
			hasNextPage,
			isLoading,
			isLoadingMore,
			loadMoreTriggerEl,
			scrollRootEl,
			() => (options.canObserve ? toValue(options.canObserve) : true),
		],
		() => {
			void setupLoadMoreObserver();
		}
	);

	onBeforeUnmount(() => {
		disconnectLoadMoreObserver();
		fetchGeneration += 1;
	});

	const getSnapshot = (): InfiniteScrollSnapshot<T> => ({
		items: [...items.value],
		total: total.value,
		nextOffset: nextOffset.value,
		hasNextPage: hasNextPage.value,
	});

	const restoreSnapshot = (snapshot: InfiniteScrollSnapshot<T>) => {
		fetchGeneration += 1;
		disconnectLoadMoreObserver();
		items.value = [...snapshot.items];
		total.value = snapshot.total;
		nextOffset.value = snapshot.nextOffset;
		hasNextPage.value = snapshot.hasNextPage;
		isLoading.value = false;
		isLoadingMore.value = false;
		loadError.value = false;
	};

	return {
		items,
		total,
		isLoading,
		isLoadingMore,
		hasNextPage,
		loadError,
		scrollRootEl,
		loadMoreTriggerEl,
		reload,
		loadMore,
		getSnapshot,
		restoreSnapshot,
	};
};
